import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Typography,
  message,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import api from "../../../api/axios";
const { Title } = Typography;
const { Option } = Select;

const AddUser = () => {
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [messageApi,contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // ====================== FETCH SHOPS & WAREHOUSES ======================
  useEffect(() => {
    const fetchData = async () => {
      try {
      const shopsRes = await api.get("/api/shops");
        setShops(shopsRes.data);

      const warehousesRes = await api.get("/api/warehouses");
        setWarehouses(warehousesRes.data);

   const usersRes = await api.get("/api/users");
        setUsers(usersRes.data);
      } catch (error) {
        console.error(error);
        messageApi.error("Failed to load shops, warehouses, or users");
      }
    };
    fetchData();
  }, []);
const visibleUsers = Array.isArray(users)
  ? users.filter((u) => u.id !== 1)
  : [];

  // ====================== ADD USER ======================
  const handleAddUser = async (values) => {
    try {
   const res = await api.post("/api/users", values);
      setUsers([...users, res.data.user]);
      messageApi.success("User added successfully!");
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to add user");
    }
  };

  // ====================== EDIT USER ======================
  const handleEdit = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleSaveEdit = async (values) => {
    try {
   const res = await api.put(`/api/users/${editingUser.id}`, values);
      setUsers((prev) =>
        prev.map((u) => (u.id === editingUser.id ? res.data.user : u))
      );
      messageApi.success("User updated successfully!");
      setEditingUser(null);
      setIsModalVisible(false);
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to update user");
    }
  };

  // ====================== TOGGLE ACTIVE ======================
const handleToggleActive = async (id, isActive) => {
  const res = await api.patch(`/api/users/${id}/toggle`, { isActive });

  setUsers((prev) =>
    prev.map((u) => (u.id === id ? res.data.user : u))
  );
};

  // ====================== TABLE COLUMNS ======================
  const columns = [

    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Shop",
      dataIndex: "shopid",
      key: "shop",
      render: (shopid) => (shopid ? shops.find((s) => s.id === shopid)?.name : "-"),
    },
    {
      title: "Warehouse",
      dataIndex: "wareid",
      key: "warehouse",
      render: (wareid) => (wareid ? warehouses.find((w) => w.id === wareid)?.name : "-"),
    },
       {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone"
    },
   {
  title: "Active",
  dataIndex: "isActive",   // optional but recommended
  key: "isActive",
  render: (_, record) => (
    <Switch
      checked={record.isActive}
      onChange={() => handleToggleActive(record.id)}
      checkedChildren="Active"
      unCheckedChildren="Inactive"
    />
  ),
},
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="default"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  // ====================== ROLE-BASED FIELDS ======================
  const renderRoleFields = (formInstance) => {
    const role = formInstance.getFieldValue("role");

    if (role === "salesmanager") {
      return (
        <>
          <Form.Item
            name="shopid"
            label="Assign Shop"
            rules={[{ required: true, message: "Select a shop!" }]}
          >
            <Select placeholder="Select Shop">
              {shops.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="warehouseid"
            label="Assign Warehouse"
            rules={[{ required: true, message: "Select a warehouse!" }]}
          >
            <Select placeholder="Select Warehouse">
              {warehouses.map((w) => (
                <Option key={w.id} value={w.id}>
                  {w.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      );
    }

    if (role === "inventorymanager") {
      return (
        <Form.Item
          name="warehouseid"
          label="Assign Warehouse"
          rules={[{ required: true, message: "Select a warehouse!" }]}
        >
          <Select placeholder="Select Warehouse">
            {warehouses.map((w) => (
              <Option key={w.id} value={w.id}>
                {w.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    return null;
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Card
        title={<Title level={4}>User List</Title>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setIsModalVisible(true);
              form.resetFields();
              setEditingUser(null);
            }}
          >
            Add User
          </Button>
        }
      >
     <Table
  dataSource={visibleUsers}
  columns={columns}
  rowKey="id"
  pagination={{ pageSize: 5 }}
/>

      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        title={editingUser ? "Edit User" : "Add User"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => {
          const currentForm = editingUser ? editForm : form;
          currentForm
            .validateFields()
            .then((values) => {
              editingUser ? handleSaveEdit(values) : handleAddUser(values);
            })
            .catch(() => message.error("Please fill required fields"));
        }}
        okText={editingUser ? "Save" : "Add"}
      >
        <Form
          layout="vertical"
          form={editingUser ? editForm : form}
          initialValues={{ role: "" }}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Please enter username!" }]}
          >
            <Input placeholder="Username" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: !editingUser, message: "Enter password!" }]}
          >
            <Input.Password placeholder={editingUser ? "Leave blank to keep current" : ""} />
          </Form.Item>

          <Form.Item
            name="role"
            label="User Role"
            rules={[{ required: true, message: "Select a role!" }]}
          >
            <Select
              placeholder="Select Role"
              onChange={() => {
                const currentForm = editingUser ? editForm : form;
                currentForm.setFieldsValue({ shop: null, warehouse: null });
              }}
            >
              <Option value="admin">Admin</Option>
              <Option value="inventorymanager">Inventory Manager</Option>
              <Option value="salesmanager">Sales Manager</Option>
                <Option value="finance">Finance</Option>
              <Option value="systemadmin">System Administrator</Option>
              <Option value="marketing">Marketing</Option>

            </Select>
          </Form.Item>
    <Form.Item
  name="phone"
  label="Phone Number"
  rules={[
    { required: true, message: "Please enter phone number!" },
    {
      pattern: /^2519\d{8}$/,
      message: "Phone number must start with 2519 and be 12 digits (e.g., 251912345678)",
    },
  ]}
>
  <Input placeholder="2519XXXXXXXX" maxLength={12} />
</Form.Item>
          {/* Dynamic Fields */}
          <Form.Item shouldUpdate={(prev, curr) => prev.role !== curr.role}>
            {({ getFieldValue }) => renderRoleFields(editingUser ? editForm : form)}
          </Form.Item>
          
        </Form>
      </Modal>
    </div>
  );
};

export default AddUser;
